import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core';
import * as React from 'react';
import { ExternalOrderService } from 'services';
import { SnackbarContext } from 'components';

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

interface HoursRow {
  dayOfWeek: number;
  fromTime: string;
  toTime: string;
  closed: boolean;
}

// ISO order, matching the API (1 = Monday .. 7 = Sunday). Bolt's own hours
// table uses 0-6 instead; do not copy an index between the two.
const DAY_LABELS: Record<number, string> = {
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
  7: 'Sunday',
};

const OFFSET_CHOICES = [0, 15, 30, 45, 60];

const trimSeconds = (s: string | null): string =>
  s && s.length >= 5 ? s.substring(0, 5) : '';

/**
 * The hours the storefront takes orders on. Editing these is what opens and
 * closes the site now, so nobody has to remember the switch. Deliberately NOT
 * the shop's opening_hours rows: those also drive reservation availability.
 */
export const WebsiteHoursDialog: React.FC<Props> = ({
  open,
  onClose,
  onSaved,
}) => {
  const snackbar = React.useContext(SnackbarContext);
  const [rows, setRows] = React.useState<HoursRow[]>([]);
  const [offset, setOffset] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const load = React.useCallback(() => {
    setLoading(true);
    ExternalOrderService.websiteGetHours()
      .then((resp: any) => {
        const byDay: Record<number, HoursRow> = {};
        (resp?.hours || []).forEach((r: any) => {
          byDay[r.dayOfWeek] = {
            dayOfWeek: r.dayOfWeek,
            fromTime: trimSeconds(r.fromTime) || '12:00',
            toTime: trimSeconds(r.toTime) || '22:30',
            closed: !!r.closed,
          };
        });
        setRows(
          Array.from({ length: 7 }, (_, i) => i + 1).map(
            (d) =>
              byDay[d] || {
                dayOfWeek: d,
                fromTime: '12:00',
                toTime: '22:30',
                closed: true,
              },
          ),
        );
        setOffset(resp?.lastOrderOffsetMinutes || 0);
      })
      .catch((e: any) => snackbar.error(e))
      .finally(() => setLoading(false));
  }, [snackbar]);

  React.useEffect(() => {
    if (open) load();
  }, [open, load]);

  const patch = (dayOfWeek: number, changes: Partial<HoursRow>) =>
    setRows((prev) =>
      prev.map((r) => (r.dayOfWeek === dayOfWeek ? { ...r, ...changes } : r)),
    );

  const save = () => {
    const bad = rows.find(
      (r) => !r.closed && (!/^\d{2}:\d{2}$/.test(r.fromTime) || !/^\d{2}:\d{2}$/.test(r.toTime)),
    );
    if (bad) {
      snackbar.error(
        new Error(`${DAY_LABELS[bad.dayOfWeek]} needs both times as HH:MM.`),
      );
      return;
    }
    setSaving(true);
    ExternalOrderService.websiteSaveHours({
      hours: rows.map((r) => ({
        day_of_week: r.dayOfWeek,
        // '1'/'0', never a raw boolean: ApiClient posts multipart/form-data,
        // where `false` arrives server-side as the string "false" and reads
        // as truthy. That shut every weekday on 2026-09-05.
        closed: r.closed ? '1' : '0',
        from_time: r.closed ? '' : r.fromTime,
        to_time: r.closed ? '' : r.toTime,
      })),
      lastOrderOffsetMinutes: offset,
    })
      .then(() => {
        snackbar.open('Ordering hours saved');
        if (onSaved) onSaved();
        onClose();
      })
      .catch((e: any) => snackbar.error(e))
      .finally(() => setSaving(false));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Website ordering hours</DialogTitle>
      <DialogContent>
        <DialogContentText>
          The ordering page opens and closes itself on these hours. Outside
          them customers can still order ahead for the next opening. These are
          separate from the restaurant opening hours used by reservations.
        </DialogContentText>

        {loading ? (
          <div style={{ padding: 24, textAlign: 'center' }}>
            <CircularProgress size={28} />
          </div>
        ) : (
          <>
            {rows.map((r) => (
              <Grid
                container
                spacing={1}
                alignItems="center"
                key={r.dayOfWeek}
                style={{ marginBottom: 4 }}
              >
                <Grid item xs={4}>
                  <Typography variant="body2">
                    {DAY_LABELS[r.dayOfWeek]}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    type="time"
                    size="small"
                    fullWidth
                    disabled={r.closed}
                    value={r.fromTime}
                    onChange={(e) =>
                      patch(r.dayOfWeek, { fromTime: e.target.value })
                    }
                    inputProps={{ step: 300 }}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    type="time"
                    size="small"
                    fullWidth
                    disabled={r.closed}
                    value={r.toTime}
                    onChange={(e) =>
                      patch(r.dayOfWeek, { toTime: e.target.value })
                    }
                    inputProps={{ step: 300 }}
                  />
                </Grid>
                <Grid item xs={2}>
                  <Checkbox
                    checked={r.closed}
                    onChange={(e) =>
                      patch(r.dayOfWeek, { closed: e.target.checked })
                    }
                  />
                  <Typography variant="caption">Closed</Typography>
                </Grid>
              </Grid>
            ))}

            <TextField
              select
              size="small"
              fullWidth
              label="Stop taking new orders before closing"
              value={offset}
              onChange={(e) => setOffset(Number(e.target.value))}
              style={{ marginTop: 16 }}
              helperText="0 means orders are taken right up to closing time."
            >
              {OFFSET_CHOICES.map((m) => (
                <MenuItem key={m} value={m}>
                  {m === 0 ? 'Right up to closing time' : `${m} minutes before`}
                </MenuItem>
              ))}
            </TextField>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={save}
          color="primary"
          variant="contained"
          disabled={saving || loading}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          Save hours
        </Button>
      </DialogActions>
    </Dialog>
  );
};
